import { useAssetVizInternal } from 'features/assetViz/AssetVizInternalProvider'
import { AssetVizMode } from 'features/assetViz/index'
import { useSurfaceProps } from 'features/assetViz/objects/surfaces/util/SurfaceProvider'
import { useSurfaceLines } from 'features/assetViz/objects/surfaces/util/useSurfaceLines'
import React from 'react'
import { GeckoComponentPolygon, GeckoComponentRect } from 'types'

import { ScaledLine3D } from './ScaledLine3D'

/**
 * Renders a rect selection on the surface of a component, mapping via its
 * surface's UVs. Note that this is not the same as a rect primitive, which is
 * derived from a markup layer.
 * @param rect              The rect being rendered
 * @param isDraft           Whether this is a draft selection or "real" one
 * @constructor
 */
export const RectSelection3D: React.FC<{
  rect: GeckoComponentRect
  isDraft: boolean
}> = ({ rect, isDraft }) => {
  //----------------------------------------------------------------------------

  const { draftSelectionColorString, selectionColorString, mode } =
    useAssetVizInternal()
  const { bounds } = useSurfaceProps()
  const path = React.useMemo(() => {
    if (GeckoComponentRect.Overlaps(rect, bounds)) {
      return GeckoComponentPolygon.CreateFromRect(rect)
    }
    return undefined
  }, [rect, bounds])

  const lines = useSurfaceLines(path ? [path] : [])

  //----------------------------------------------------------------------------

  return lines === undefined || lines.xyzs.length === 0 ? null : (
    <ScaledLine3D
      key={rect.componentID}
      points={lines.xyzs}
      linewidth={3}
      dashed={
        // Only use a dashed line if we're in a rect selection/drawing mode
        isDraft &&
        (mode === AssetVizMode.SelectRect ||
          mode === AssetVizMode.CreateMarkupRect)
      }
      dashScale={5}
      color={isDraft ? draftSelectionColorString : selectionColorString}
    />
  )
}
